import { Link, useNavigate } from 'react-router-dom';
import MaterialCard from '../components/MaterialCard/MaterialCard';
import InstantSearchWrapper from '../components/layout/Search/InstantSearchWrapper';
import { useEffect, useState } from 'react';
import {
    getCategories,
    getFeaturedMaterials,
    getSubcategories,
} from '../services/algolia';
import { FinishCard } from '../components/FinishCard/FinishCard';
import { AlgoliaFinishHit, AlgoliaPaletteHit } from '../types/AlgoliaTypes';
import {
    getLanguageSubtypesConfig,
    getSubtypesConfig,
} from '../services/appConfig';
import Search from '../components/Search/Search';
import { algoliaConfig } from '../data/config';
import { getFeaturedPalettes } from '../services/algoliaPaletteService';
import { getCategoryPage } from '../utils/utils';

import { ReactComponent as RightCaret } from '../assets/images/right-caret.svg';

interface Subtype {
    title: string;
    img?: string;
    parentCategory?: string;
    route: string;
    isHidden?: boolean
}

interface ParentCategory {
    id: number;
    parentCategoryName: string;
    categoryCode: string;
    route: string;
}

export default function Overview() {
    const [materials, setMaterials] = useState<Subtype[]>();
    const [subcategoryLength, setSubcategoryLength] =
        useState<Map<string, number>>();
    const [fabricRegionalName, setFabricRegionalName] =
        useState<string>('Fabric');
    const [laminateRegionalName, setLaminateRegionalName] =
        useState<string>('Laminate & Wood');
    const [subtypes, setSubtypes] = useState<ParentCategory[]>([]);
    const [activeSubtype, setActiveSubtype] = useState<ParentCategory>({
        id: 1,
        parentCategoryName: 'All',
        categoryCode: '',
        route: '',
    });
    const [shownItems, setShownItems] = useState<number>(6);
    const [featuredFabrics, setFeaturedFabrics] = useState<AlgoliaFinishHit[]>(
        [],
    );
    const [featuredLaminates, setFeaturedLaminates] =
        useState<AlgoliaFinishHit[]>();
    const [featuredPalettes, setFeaturedPalettes] =
        useState<AlgoliaPaletteHit[]>();
    const navigate = useNavigate();

    useEffect(() => {
        function findImageByTitle(
            subtypes: Subtype[],
            targetTitle: string,
        ): string {
            const foundSubtype = subtypes.find(
                (subtype) => subtype.title === targetTitle,
            );
            return foundSubtype?.img ?? '';
        }

        function findIfSubtypeIsHiddenByTitle(
            subtypes: Subtype[],
            targetTitle: string,
        ): boolean {
            const foundSubtype = subtypes.find(
                (subtype) => subtype.title === targetTitle,
            );
            return foundSubtype?.isHidden ?? false;
        }

        const getMaterials = async () => {
            const parentCategories: ParentCategory[] = [
                {
                    id: 1,
                    parentCategoryName: 'All',
                    categoryCode: '',
                    route: '',
                },
                {
                    id: 2,
                    parentCategoryName: 'Fabric',
                    categoryCode: getCategoryPage('fabric'),
                    route: 'fabric',
                },
                {
                    id: 3,
                    parentCategoryName: 'Laminate & Wood',
                    categoryCode: getCategoryPage('laminate_wood'),
                    route: 'laminate-wood',
                },
                {
                    id: 4,
                    parentCategoryName: 'Paint',
                    categoryCode: getCategoryPage('paint'),
                    route: 'paint',
                },
                {
                    id: 5,
                    parentCategoryName: 'More Surfaces',
                    categoryCode: getCategoryPage('more_surfaces'),
                    route: 'more-surfaces',
                },
            ];

            const subtypes: Subtype[] = [];

            const data = await getSubtypesConfig();
            let jsonValue: Subtype[] = [];
            if (data && data.statusCode === 200 && data.value) {
                jsonValue = JSON.parse(data.value);
            }

            let languageJsonValue: Subtype[];

            if (window.steelcase_finish_library.siteUrl.path.includes('de')) {
                const SubtypesData = await getLanguageSubtypesConfig('DE');
                if (
                    SubtypesData &&
                    SubtypesData.statusCode === 200 &&
                    SubtypesData.value
                ) {
                    languageJsonValue = JSON.parse(SubtypesData.value);
                }
            } else if (
                window.steelcase_finish_library.siteUrl.path.includes('fr')
            ) {
                const SubtypesData = await getLanguageSubtypesConfig('FR');
                if (
                    SubtypesData &&
                    SubtypesData.statusCode === 200 &&
                    SubtypesData.value
                ) {
                    languageJsonValue = JSON.parse(SubtypesData.value);
                }
            } else if (
                window.steelcase_finish_library.siteUrl.path.includes('es')
            ) {
                const SubtypesData = await getLanguageSubtypesConfig('ES');
                if (
                    SubtypesData &&
                    SubtypesData.statusCode === 200 &&
                    SubtypesData.value
                ) {
                    languageJsonValue = JSON.parse(SubtypesData.value);
                }
            } else {
                if (data && data.statusCode === 200 && data.value) {
                    languageJsonValue = JSON.parse(data.value);
                }
            }

            await Promise.all(
                parentCategories.map(async (item) => {
                    if (item.categoryCode) {
                        const data = await getCategories(item.categoryCode);
                        // Get category label
                        if (data && data['category.label']) {
                            const [parentCategoryKey] = Object.entries(
                                data['category.label'],
                            );

                            if (item.parentCategoryName === 'Fabric')
                                setFabricRegionalName(parentCategoryKey[0]);
                            if (item.parentCategoryName === 'Laminate & Wood')
                                setLaminateRegionalName(parentCategoryKey[0]);

                            item.parentCategoryName = parentCategoryKey[0];
                            // Get subcategory labels for parent category
                            const subcategoriesFromAlgolia =
                                await getSubcategories(item.categoryCode);

                            if (
                                subcategoriesFromAlgolia &&
                                subcategoriesFromAlgolia['filters.subcategory']
                            ) {
                                const subcategoryObject = Object.entries(
                                    subcategoriesFromAlgolia[
                                        'filters.subcategory'
                                    ],
                                );
                                subcategoryObject.map((subTitle) => {
                                    subtypes.push({
                                        title: subTitle[0],
                                        parentCategory: parentCategoryKey[0],
                                        img: findImageByTitle(
                                            [
                                                ...jsonValue,
                                                ...languageJsonValue,
                                            ],
                                            subTitle[0],
                                        ),
                                        route: item.route,
                                        isHidden: findIfSubtypeIsHiddenByTitle(
                                            [
                                                ...jsonValue,
                                                ...languageJsonValue
                                            ],
                                            subTitle[0]
                                        )
                                    });
                                });
                            }
                        }
                    }
                    return item;
                }),
            );

            setSubtypes(parentCategories);
            setMaterials(subtypes);

            const map = new Map<string, number>();
            subtypes.forEach((item) => {
                if (item.parentCategory && map.has(item.parentCategory)) {
                    const currentCount = map.get(item.parentCategory);
                    if (currentCount)
                        map.set(item.parentCategory, currentCount + 1);
                } else {
                    if (item.parentCategory) map.set(item.parentCategory, 1);
                }
            });
            map.set('All', subtypes.length);
            setSubcategoryLength(map);
        };
        getMaterials();
    }, []);

    useEffect(() => {
        const getFabrics = async () => {
            const values = await getFeaturedMaterials(
                getCategoryPage('fabric'),
            );
            const featuredFabrics = values.hits as AlgoliaFinishHit[];
            featuredFabrics.sort(
                (a, b) => a.featuredOrder.value - b.featuredOrder.value,
            );
            setFeaturedFabrics(featuredFabrics.slice(0, 4));
        };

        const getLaminates = async () => {
            const values = await getFeaturedMaterials(
                getCategoryPage('laminate_wood'),
            );
            const featuredLaminates = values.hits as AlgoliaFinishHit[];
            featuredLaminates.sort(
                (a, b) => a.featuredOrder.value - b.featuredOrder.value,
            );
            setFeaturedLaminates(featuredLaminates.slice(0, 4));
        };

        const getPalettes = async () => {
            const values = await getFeaturedPalettes();
            const featuredPalettes = values.hits as AlgoliaPaletteHit[];
            featuredPalettes.sort(
                (a, b) => a.featuredOrder.value - b.featuredOrder.value,
            );
            setFeaturedPalettes(featuredPalettes.slice(0, 7));
        };

        getFabrics();
        getLaminates();
        getPalettes();
    }, []);

    const handleShowMore = () => {
        let currentSubcategoryLength;
        if (subcategoryLength) {
            currentSubcategoryLength = subcategoryLength.get(
                activeSubtype.parentCategoryName,
            );
        }

        if (
            currentSubcategoryLength &&
            currentSubcategoryLength <= shownItems + 12
        ) {
            setShownItems(currentSubcategoryLength + 1);
        } else {
            setShownItems((prevState) => prevState + 12);
        }
    };

    return (
        <InstantSearchWrapper indexName={algoliaConfig.MATERIAL_INDEX}>
            <div
                className={'fl-container__flex fl-container__content'}
                style={{ marginTop: '20px', marginBottom: '20px' }}
            >
                <Search />
                <div className={'finish-overview__header'}>
                    <h2>
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .overview_page.overview_heading
                        }
                    </h2>
                    <p>
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .overview_page.overview_sub_heading
                        }
                    </p>
                </div>
                <ul className={'finish-overview__header-list'}>
                    {subtypes.map((subtype) => {
                        return (
                            <li
                                key={subtype.id}
                                className={`finish-overview__header-list-item ${
                                    activeSubtype.parentCategoryName ===
                                    subtype.parentCategoryName
                                        ? 'finish-overview__header-list-item--active'
                                        : ''
                                }`}
                                onClick={() => {
                                    setActiveSubtype(subtype);
                                    if (
                                        subcategoryLength?.get(
                                            subtype.parentCategoryName,
                                        )! < 6
                                    ) {
                                        setShownItems(
                                            subcategoryLength?.get(
                                                subtype.parentCategoryName,
                                            )!,
                                        );
                                    } else {
                                        setShownItems(6);
                                    }
                                }}
                            >
                                {subtype.parentCategoryName === 'All' &&
                                window.steelcase_i18n[
                                    'steelcase-finish-library'
                                ].all
                                    ? window.steelcase_i18n[
                                          'steelcase-finish-library'
                                      ].all
                                    : subtype.parentCategoryName}
                            </li>
                        );
                    })}
                </ul>
                <div className={'finish-material-card__grid-wrapper'}>
                    {materials && activeSubtype.parentCategoryName === 'All'
                        ? materials.filter((subtype) => !subtype.isHidden).slice(0, shownItems).map((item, idx) => {
                              return (
                                  <MaterialCard
                                      key={idx}
                                      title={item.title}
                                      img={
                                          item.img
                                              ? item.img
                                              : 'https://images.steelcase.com/image/upload/v1640120664/www.steelcase.com/2021/12/21/swatch-placeholder.png'
                                      }
                                      onClickHandler={() =>
                                          navigate(
                                              `/finishes/${item.route}?subcategory=${item.title}`,
                                          )
                                      }
                                  />
                              );
                          })
                        : materials &&
                          materials
                              .filter((subtype) => !subtype.isHidden)
                              .filter(
                                  (mat) =>
                                      mat.parentCategory ===
                                      activeSubtype.parentCategoryName,
                              )
                              .slice(0, shownItems)
                              .map((item, idx) => {
                                  return (
                                      <MaterialCard
                                          key={idx}
                                          title={item.title}
                                          img={
                                              item.img
                                                  ? item.img
                                                  : 'https://images.steelcase.com/image/upload/v1640120664/www.steelcase.com/2021/12/21/swatch-placeholder.png'
                                          }
                                          onClickHandler={() =>
                                              navigate(
                                                  `/finishes/${item.route}?subcategory=${item.title}`,
                                              )
                                          }
                                      />
                                  );
                              })}
                </div>
                {subcategoryLength &&
                subcategoryLength.has(activeSubtype.parentCategoryName) &&
                subcategoryLength.get(activeSubtype.parentCategoryName)! + 1 >=
                    shownItems &&
                !(
                    subcategoryLength?.get(activeSubtype.parentCategoryName)! <=
                    6
                ) ? (
                    <button
                        className={'finish-details__swatch-show-more-button'}
                        onClick={() =>
                            shownItems >=
                            subcategoryLength?.get(
                                activeSubtype.parentCategoryName,
                            )!
                                ? setShownItems(6)
                                : handleShowMore()
                        }
                    >
                        {shownItems >=
                        subcategoryLength?.get(
                            activeSubtype.parentCategoryName,
                        )!
                            ? `${window.steelcase_i18n['steelcase-finish-library'].show_less} -`
                            : `${window.steelcase_i18n['steelcase-finish-library'].show_more} +`}
                    </button>
                ) : null}
                <div className={'finish-overview__featured__heading'}>
                    <h3>
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .palettes.popular_palette
                        }
                    </h3>
                    <Link
                        to={'/palettes'}
                        className={'finish-overview__featured__view-all'}
                    >
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .palettes.view_all_palettes
                        }{' '}
                        <RightCaret />{' '}
                    </Link>
                    <div
                        className={
                            'finish-overview__featured__palettes-wrapper'
                        }
                    >
                        {featuredPalettes &&
                            featuredPalettes?.slice(0, 2).map((item, idx) => {
                                return (
                                    <div
                                        key={`${item.name}-${idx}`}
                                        className={
                                            'finish-overview__featured__palettes-card-container finish-overview__featured__palettes-card-container--half'
                                        }
                                    >
                                        <MaterialCard
                                            title={item.name}
                                            img={item.assetRelationships[0]}
                                            onClickHandler={() =>
                                                navigate(
                                                    `/palettes/${item.routerValue}`,
                                                )
                                            }
                                        />
                                    </div>
                                );
                            })}
                    </div>

                    <div
                        className={
                            'finish-overview__featured__palettes-wrapper'
                        }
                    >
                        {featuredPalettes &&
                            featuredPalettes?.slice(2, 7).map((item, idx) => {
                                return (
                                    <div
                                        key={`${item.name}-${idx}`}
                                        className={
                                            'finish-overview__featured__palettes-card-container'
                                        }
                                    >
                                        <MaterialCard
                                            title={item.name}
                                            img={item.assetRelationships[0]}
                                            onClickHandler={() =>
                                                navigate(
                                                    `/palettes/${item.routerValue}`,
                                                )
                                            }
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </div>

                <div className={'finish-overview__featured__heading'}>
                    <h3>
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .featured_fabric
                        }
                    </h3>
                    <Link
                        to={'/finishes/fabric'}
                        className={'finish-overview__featured__view-all'}
                    >
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .view_all_fabrics
                        }{' '}
                        <RightCaret />{' '}
                    </Link>
                </div>
                <div className={'finish-card__list'}>
                    {featuredFabrics.map((fabric, idx) => {
                        return (
                            <FinishCard
                                finish={fabric}
                                key={`${fabric.objectID}-fabric-${idx}`}
                            />
                        );
                    })}
                </div>

                <div className={'finish-overview__featured__heading'}>
                    <h3>
                        {
                            window.steelcase_finish_library.siteUrl.path === "/" ? "Featured Laminate & Wood" : window.steelcase_i18n['steelcase-finish-library']
                                .featured_laminate_wood
                        }
                    </h3>
                    <Link
                        to={'/finishes/laminate-wood'}
                        className={'finish-overview__featured__view-all'}
                    >
                        {
                            window.steelcase_i18n['steelcase-finish-library']
                                .view_all_laminates_wood
                        }{' '}
                        <RightCaret />
                    </Link>
                </div>
                <div className={'finish-card__list'}>
                    {featuredLaminates?.map((laminate, idx) => {
                        return (
                            <FinishCard
                                finish={laminate}
                                key={`${laminate.objectID}-laminate-${idx}`}
                            />
                        );
                    })}
                </div>
            </div>
        </InstantSearchWrapper>
    );
}
